import { useState } from 'react';
import { Anchor, Button, Group, Stack, Text, Tooltip } from '@mantine/core';
import { Link } from 'react-router-dom';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useAddCounterpartyToDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useAddCounterpartyToDocument';
import { AddCounterpartyModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyModal';
import {
	REFUSAL_COPY,
	REVOKE_REFUSALS,
	type AddCounterpartyRefusalCode,
	type AssignableUser,
	type SlotSubmission,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyModal.types';
import type { CounterpartyPlacementPayload } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/useCounterpartyPlacement.types';
import { EligibleDocumentFilterMode } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import type { DocumentsNavigationState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.types';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';

interface Props {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	assignableUsers: AssignableUser[];
	/** Carries the roles and users that were filled, so the caller can wait on the asynchronous
	 *  signature-sheet regeneration a wet-ink assignment kicks off. */
	onAdded: (added: { roleId: string; assignedUserIds: string[] }) => void;
}

const isKnownRefusal = (reason: string): reason is AddCounterpartyRefusalCode =>
	reason in REFUSAL_COPY;

export const AddCounterpartyAction = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	assignableUsers,
	onAdded,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const tMipManage = useTranslationMipManage();
	const { eligibility, addCounterparty } = useAddCounterpartyToDocument({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
	});

	const rawReason = eligibility.data?.reason;
	const refusal = rawReason && isKnownRefusal(rawReason) ? rawReason : null;
	const canAdd = eligibility.data?.canAdd ?? false;

	const handleSubmit = async (values: {
		name: string;
		slots: SlotSubmission[];
		placements: CounterpartyPlacementPayload[];
	}) => {
		const result = await addCounterparty.mutateAsync({
			path: { dealId, programmeId, documentWorkflowInstanceId },
			body: {
				name: values.name,
				slots: values.slots,
				// Omitted rather than sent empty — a document that signs on the appended
				// signature page has no boxes to report.
				...(values.placements.length > 0 ? { placements: values.placements } : {}),
			},
		});
		setIsOpen(false);
		onAdded({
			roleId: result.roleId,
			assignedUserIds: values.slots
				.map((slot) => slot.userId)
				.filter((userId): userId is string => Boolean(userId)),
		});
	};

	// Nothing to offer and nothing to explain: the eligibility query hasn't resolved, 403'd for a
	// viewer, or 400'd because the document has no signing configuration at all. Every document in
	// the product renders this component, so those cases must leave no trace. Every refusal that
	// does resolve is shown — the manager is owed the reason and the way out.
	if (!eligibility.data) {
		return null;
	}

	// The action isn't allowed but the manager can act on it — always show *some* explanation: a
	// known refusal's exact copy, or a neutral fallback for a reason the union hasn't caught up to.
	const explanation = canAdd
		? null
		: (refusal && REFUSAL_COPY[refusal]) ||
			tMipManage('documents_page.add_counterparty.unavailable_fallback');

	return (
		<Stack gap="xs" align="flex-start">
			<Button
				variant="default"
				onClick={() => setIsOpen(true)}
				disabled={!canAdd}
				data-testid={TEST_ELEMENT_IDS.ADD_COUNTERPARTY_ACTION_BUTTON}
			>
				{tMipManage('documents_page.add_counterparty.action_button')}
			</Button>

			{explanation && (
				<Group gap={4} wrap="nowrap">
					<Text
						size="0.75rem"
						c={defaultTheme.v3.colors.black}
						data-testid={TEST_ELEMENT_IDS.ADD_COUNTERPARTY_REFUSAL_MESSAGE}
					>
						{explanation}
					</Text>
					{rawReason && REVOKE_REFUSALS.has(rawReason) && (
						<Tooltip
							label={tMipManage('documents_page.add_counterparty.revoke_link')}
							withArrow
						>
							{/* The copy is the tooltip, so aria-label carries it too — without it
							    the link has no text and announces as nothing. */}
							<Anchor
								component={Link}
								display="flex"
								aria-label={tMipManage(
									'documents_page.add_counterparty.revoke_link'
								)}
								to={mipManagePath(MIP_MANAGE_ROUTES.DOCUMENTS, {
									dealId,
									mipId: programmeId,
								})}
								// Router state, not a query param: the documents list holds no URL
								// state at all, and this is a click-through intent rather than a
								// link anyone should bookmark.
								state={
									{
										enterEligibleDocumentFilterMode:
											EligibleDocumentFilterMode.REVOKE,
									} satisfies DocumentsNavigationState
								}
								data-testid={TEST_ELEMENT_IDS.ADD_COUNTERPARTY_REVOKE_LINK}
							>
								<Icon variant="refresh-v3" size="xl" />
							</Anchor>
						</Tooltip>
					)}
				</Group>
			)}
			{/* Mounted only when the action is live — the slot cap comes off the same eligibility
			    read, so a modal opened without it would offer counts the server would refuse. */}
			{canAdd && (
				<AddCounterpartyModal
					isOpen={isOpen}
					onClose={() => setIsOpen(false)}
					dealId={dealId}
					placementRequirement={eligibility.data.placement ?? null}
					maxSlots={eligibility.data.maxSlots}
					freeRows={eligibility.data.freeRows}
					totalRows={eligibility.data.totalRows}
					assignableUsers={assignableUsers}
					isSubmitting={addCounterparty.isPending}
					onSubmit={handleSubmit}
				/>
			)}
		</Stack>
	);
};
